import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { Logo } from '@/components/synapse/Logo';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { PatientDetailsFields } from '@/features/patients/PatientDetailsFields';
import {
  emptyPatientDetails,
  hasPatientDetailsErrors,
  resolveDateOfBirth,
  validatePatientDetails,
  type PatientDetailsDraft,
  type PatientDetailsErrors,
} from '@/features/patients/patientDetails';
import {
  usePublicClinic,
  useSubmitSelfRegistration,
} from '@/features/patients/useSelfRegistrations';
import { readRegistrationLanguage } from '@/features/patients/registrationLink';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { useLanguageStore } from '@/stores/languageStore';
import { TENANT } from '@/routing/tenant';

/**
 * Public patient self-registration. Reached by scanning the clinic's printed QR;
 * no session, no app shell, no navigation.
 *
 * A submission does NOT create a patient — it queues for staff review. That is
 * what lets an anonymous form exist at all: the resulting patient record still
 * gets a real staff member as its `created_by`, and a human checks for
 * duplicates before anything lands in the registry.
 *
 * The field set is shared with the staff registration form via
 * PatientDetailsFields, minus the photo — an anonymous presigned S3 upload is a
 * much larger surface than the rest of this page, and reception can capture the
 * photo at the counter where the authenticated upload path already works.
 */
export function PublicRegisterPage() {
  const { t } = useTranslation();
  // The clinic comes from the URL's first segment, not a route param: this page
  // lives at `/<slug>/p/register`, so the router's basename has already taken it.
  const clinicRef = TENANT.slug ?? undefined;
  const [searchParams] = useSearchParams();
  const { data: clinic, isLoading, isError } = usePublicClinic(clinicRef);
  const submitMutation = useSubmitSelfRegistration(clinicRef);

  // A poster printed in Tamil carries `?lang=ta`, so the form opens in the
  // language the patient was reading a second ago. Applied through the language
  // store rather than i18n directly, so the toggle in the header agrees with
  // what is on screen.
  //
  // Keyed on the parameter alone: once applied, a patient who then picks a
  // different language from the toggle is not overruled on the next render.
  const qrLanguage = readRegistrationLanguage(searchParams);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  useEffect(() => {
    if (qrLanguage) setLanguage(qrLanguage);
  }, [qrLanguage, setLanguage]);

  const [details, setDetails] = useState<PatientDetailsDraft>(() => emptyPatientDetails());
  const [fieldErrors, setFieldErrors] = useState<PatientDetailsErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const fullName = details.fullName.trim();
    const errors: PatientDetailsErrors = {
      ...validatePatientDetails(details),
      fullName: fullName ? null : t('publicRegister.nameRequired'),
    };
    setFieldErrors(errors);
    // The public form has no staff member to interpret a 400, so a malformed NIC
    // has to be caught and explained here or it reads as "the site is broken".
    if (hasPatientDetailsErrors(errors)) return;

    const dateOfBirth = resolveDateOfBirth(details.dob, details.age);
    const phone = details.phone.trim();
    const nationalId = details.nationalId.trim();

    try {
      await submitMutation.mutateAsync({
        fullName,
        sex: details.sex,
        ...(dateOfBirth != null ? { dateOfBirth } : {}),
        ...(phone ? { phone } : {}),
        ...(nationalId ? { nationalId } : {}),
      });
      setDone(true);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? translateApiError(err, { fallback: t('publicRegister.submitFailed') })
          : t('publicRegister.submitFailed'),
      );
    }
  }

  if (isLoading) {
    return (
      <Shell>
        <p className="text-[13.5px] text-muted">{t('publicRegister.loading')}</p>
      </Shell>
    );
  }

  // A bad or retired link. Deliberately a dead end rather than a redirect —
  // there is nowhere useful to send someone who is not a staff member.
  if (isError || !clinic) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
            <Icon name="alert" size={22} />
          </div>
          <h1 className="text-lg font-semibold tracking-[-0.02em]">
            {t('publicRegister.linkInvalid')}
          </h1>
          <p className="max-w-[340px] text-[13.5px] leading-relaxed text-muted">
            {t('publicRegister.linkInvalidBody')}
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell clinicName={clinic.clinicName}>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-success">
            <Icon name="check_circle" size={26} />
          </div>
          <h1 className="text-xl font-semibold tracking-[-0.02em]">
            {t('publicRegister.doneTitle')}
          </h1>
          {/* Nothing the patient typed is echoed back — a shared waiting-room
              phone must not leave the previous person's details on screen. */}
          <p className="max-w-[360px] text-[13.5px] leading-relaxed text-muted">
            {t('publicRegister.doneBody')}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell clinicName={clinic.clinicName}>
      <header className="mb-5">
        <h1 className="text-xl font-semibold tracking-[-0.02em] sm:text-2xl">
          {t('publicRegister.heading')}
        </h1>
        <p className="mt-1 text-[13.5px] text-muted">{t('publicRegister.intro')}</p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        <PatientDetailsFields
          value={details}
          onChange={setDetails}
          errors={fieldErrors}
          disabled={submitMutation.isPending}
          autoFocusName
        />

        <div className="flex items-center gap-2.5 rounded-md border border-line bg-paper-2 px-3.5 py-3">
          <Icon name="info" size={16} className="shrink-0 text-info" />
          <span className="text-[12.5px] leading-snug text-muted">
            {t('publicRegister.privacyNote')}
          </span>
        </div>

        {submitError && (
          <p className="text-[12.5px] text-destructive" role="alert">
            {submitError}
          </p>
        )}

        <Button type="submit" full disabled={submitMutation.isPending}>
          {submitMutation.isPending
            ? t('publicRegister.submitting')
            : t('publicRegister.submit')}
        </Button>
      </form>
    </Shell>
  );
}

/**
 * Full-screen chrome for the public page. Mirrors PublicCounterPage: no
 * AppShell, no nav, nothing that hints at a staff area. The language toggle is
 * present because the person reading this is a patient choosing their own
 * language, not a staff member with a saved preference.
 */
function Shell({
  clinicName,
  children,
}: {
  clinicName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <div className="mx-auto w-full max-w-[560px]">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {/* The clinic's name is what identifies this page to a patient;
                the product wordmark would only compete with it. */}
            <Logo size={26} withWord={!clinicName} />
            {clinicName && (
              <span className="truncate text-[13.5px] font-medium text-ink-2">
                {clinicName}
              </span>
            )}
          </div>
          <LanguageToggle />
        </div>
        {children}
      </div>
    </div>
  );
}
